/**
 * Calibration for the slow-connection choice sheet ("This connection may take
 * a while" → Continue / I'll use Wi-Fi later).
 *
 * ── Why these numbers moved ────────────────────────────────────────────────
 * The original trigger — `etaSeconds >= 90 || mbpsEwma <= 3.5` — was tuned in
 * June 2026, while Chromium-on-4g clients were seeded with a *synthetic*
 * 8 Mbps upload speed rather than a measured one. Against that inflated
 * baseline almost nothing looked slow, so in the data the sheet appeared rare
 * and 3.5 Mbps / 90 s read as a conservative floor.
 *
 * PR #2378 (Jun 22 2026) replaced the synthetic seed with real measurements,
 * and the picture inverted: real cellular uploaders sit at a median of roughly
 * 4–5 Mbps, with ~35–40% below 3.5 Mbps. The same thresholds that looked rare
 * were now interrupting the majority of slow-line uploads. Crucially,
 * completion rate held flat across that change — the extra sheets were not
 * rescuing uploads that would otherwise have failed. They were noise, shown
 * mid-upload to people whose uploads were going to finish either way.
 *
 * ── Recalibration (Aug 2026) ───────────────────────────────────────────────
 * The sheet is an interruption, so it should only appear where the odds of
 * finishing genuinely collapse — not merely where the line is below average.
 *
 *  - `SLOW_MBPS_THRESHOLD` (1.5): sits well under the 4–5 Mbps real-world
 *    median instead of straddling it, so an ordinary cellular uploader never
 *    trips it. Below ~1.5 Mbps a typical clip stops finishing inside any
 *    plausible attention span, which is where deferring to Wi-Fi is a real
 *    service rather than a distraction.
 *  - `SLOW_ETA_SECONDS_THRESHOLD` (240): four minutes. The old 90 s fired on
 *    uploads that were merely a bit long. Four minutes is long enough that
 *    "save progress and finish on Wi-Fi" beats waiting it out.
 *  - `SLOW_CONSECUTIVE_TICKS` (3): one chunk measurement is noisy — a single
 *    tower handoff, a backgrounded tab, or one oversized part drags the EWMA
 *    under the line for a tick and then recovers. Requiring three consecutive
 *    slow measurements filters those transients while still surfacing within a
 *    handful of parts on a genuinely bad link.
 *
 * The sheet is additionally one-shot per upload (see the latch in
 * `useHandleSubmitUploadedFile` and `choiceSheetDismissed` in
 * `UploadFlowCarousel`): having been offered the choice once and declined it,
 * the user should not be asked again mid-upload.
 */

/** Smoothed throughput (Mbps) at or below which the link counts as collapsing. */
export const SLOW_MBPS_THRESHOLD = 1.5;

/** Projected remaining seconds at or above which the link counts as collapsing. */
export const SLOW_ETA_SECONDS_THRESHOLD = 240;

/** Consecutive slow chunk measurements required before the sheet may open. */
export const SLOW_CONSECUTIVE_TICKS = 3;

/**
 * Instantaneous "is this link collapsing?" test for a single chunk
 * measurement. Callers are responsible for requiring
 * `SLOW_CONSECUTIVE_TICKS` of these in a row before acting — one reading on
 * its own is not evidence.
 *
 * `mbpsEwma <= 0` means "not measured yet" (the Upload instance reports 0
 * until the first chunk lands), which must not count as slow — otherwise the
 * very first tick of every upload reads as a collapsing link.
 */
export const isCollapsingConnection = (mbpsEwma: number, etaSeconds: number | null): boolean =>
  (etaSeconds !== null && etaSeconds >= SLOW_ETA_SECONDS_THRESHOLD) ||
  (mbpsEwma > 0 && mbpsEwma <= SLOW_MBPS_THRESHOLD);

/* ── Mid-chunk detection ─────────────────────────────────────────────────────
 * The consecutive-ticks rule above counts CHUNK COMPLETIONS — but on the very
 * links the sheet exists for, chunks are minutes apart (a 5 MiB part at
 * 0.4 Mbps is ~104 s), so three ticks can never accumulate before the user
 * gives up. Progress events are the mid-chunk signal: sample the reported
 * uploaded bytes and measure a sliding-window drain rate.
 *
 * Why a window and a delay instead of bytes-since-start: XHR "progress" counts
 * bytes handed to the SOCKET, so the first ~1–2 MB land instantly in kernel
 * buffers and inflate any since-start average. After the buffers fill, each
 * new progress delta reflects the true drain rate of the link. The observation
 * delay skips the burst; the window measures only recent deltas.
 */

/** Ignore the first stretch of an upload — socket buffering inflates it. */
export const SLOW_OBSERVE_DELAY_MS = 12_000;
/** Sliding window over which the drain rate is measured. */
export const SLOW_WINDOW_MS = 10_000;
/** Minimum sample span before a window rate is trusted at all. */
export const SLOW_MIN_SPAN_MS = 6_000;
/** The window rate must read as collapsing for this long, uninterrupted. */
export const SLOW_SUSTAIN_MS = 10_000;

export interface RateSample {
  /** Wall-clock ms. */
  t: number;
  /** Monotonic reported uploaded bytes at that moment. */
  bytes: number;
}

/**
 * Drain rate (Mbps) across the sampled window, or null when the window is too
 * short to trust. Callers prune `samples` to SLOW_WINDOW_MS before calling.
 */
export const windowRateMbps = (samples: RateSample[]): number | null => {
  if (samples.length < 2) return null;
  const first = samples[0];
  const last = samples[samples.length - 1];
  const spanMs = last.t - first.t;
  if (spanMs < SLOW_MIN_SPAN_MS) return null;
  return ((last.bytes - first.bytes) * 8) / (spanMs / 1000) / 1_000_000;
};
